import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Container, Typography, ImageList, ImageListItem, ImageListItemBar, IconButton, CircularProgress, Box, Tooltip } from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { AnimatePresence, motion } from "framer-motion";
import { mediaThumb, downloadImage } from "../../services/media";

const API = "https://figazuresql.swancreate.com/api/crm";

export default function GalleryDetail() {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(-1); // index of the photo open in the lightbox, or -1
  const [lowResLoaded, setLowResLoaded] = useState(false); // instant cached thumbnail painted
  const [hiResLoaded, setHiResLoaded] = useState(false); // sharp full-size lightbox image finished loading

  useEffect(() => {
    setLoading(true);
    setGallery(null);
    setImages([]);
    axios.get(`${API}/sk12_gallerymst?Slug=${encodeURIComponent(slug)}`).then((r) => {
      const g = (r.data || []).find((x) => x.IsPublished);
      setGallery(g || null);
      if (g) {
        axios.get(`${API}/sk12_galleryimages?GalleryId=${g.apiid}`)
          .then((ir) => setImages(ir.data || []))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }).catch(() => setLoading(false));
  }, [slug]);

  const close = useCallback(() => setActive(-1), []);
  const prev = useCallback(() => setActive((i) => (i <= 0 ? images.length - 1 : i - 1)), [images.length]);
  const next = useCallback(() => setActive((i) => (i >= images.length - 1 ? 0 : i + 1)), [images.length]);

  // Reset the hi-res load state whenever the open photo changes, and preload the
  // neighbours so left/right navigation feels instant.
  useEffect(() => {
    if (active < 0) return;
    setLowResLoaded(false);
    setHiResLoaded(false);
    [active - 1, active + 1].forEach((i) => {
      const n = images[(i + images.length) % images.length];
      if (n) { const im = new Image(); im.src = mediaThumb(n.ImageUrl, 1400); }
    });
  }, [active, images]);

  // Keyboard navigation while the lightbox is open.
  useEffect(() => {
    if (active < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, prev, next]);

  if (loading) return <Container sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Container>;
  if (!gallery) return <Container sx={{ py: 6 }}><Typography>Gallery not found.</Typography></Container>;

  const current = active >= 0 ? images[active] : null;

  const dlBtnSx = {
    color: "white",
    bgcolor: "rgba(0,0,0,0.35)",
    backdropFilter: "blur(4px)",
    border: "1px solid rgba(255,255,255,0.25)",
    transition: "background-color .2s ease, transform .2s ease",
    "&:hover": { bgcolor: "rgba(0,0,0,0.6)", transform: "translateY(-1px)" },
  };

  return (
    <Container sx={{ py: 6 }}>
      <Typography variant="h2" sx={{ mb: 1 }}>{gallery.Title}</Typography>
      {gallery.Description && <Typography color="text.secondary" sx={{ mb: 3 }}>{gallery.Description}</Typography>}
      <ImageList variant="masonry" cols={3} gap={12}>
        {images.map((img, i) => (
          <ImageListItem key={img.apiid} sx={{ borderRadius: 2, overflow: "hidden" }}>
            {/* Thumbnail for fast display; the lightbox/download serves the full-resolution original. */}
            <Box
              component="img"
              src={mediaThumb(img.ImageUrl, 700)}
              alt={img.Caption || gallery.Title}
              loading="lazy"
              onClick={() => setActive(i)}
              sx={{
                display: "block",
                width: "100%",
                cursor: "zoom-in",
                transition: "transform .35s ease, filter .35s ease",
                "&:hover": { transform: "scale(1.03)", filter: "brightness(1.05)" },
              }}
            />
            <ImageListItemBar
              sx={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))" }}
              position="bottom"
              actionIcon={
                <Tooltip title="Download full-resolution photo">
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); downloadImage(img.ImageUrl, img.Caption); }}
                    sx={{ ...dlBtnSx, m: 1 }}
                    aria-label="Download photo"
                  >
                    <FileDownloadRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
          </ImageListItem>
        ))}
      </ImageList>
      {images.length === 0 && <Typography color="text.secondary">No photos yet.</Typography>}

      {/* Lightbox */}
      <AnimatePresence>
        {current && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: (t) => t.zIndex.modal + 1,
              bgcolor: "rgba(10,12,16,0.97)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 1, sm: 4 },
            }}
          >
            {/* Close */}
            <IconButton
              onClick={close}
              aria-label="Close"
              sx={{ position: "absolute", top: 12, right: 12, color: "white", bgcolor: "rgba(255,255,255,0.08)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
            >
              <CloseRoundedIcon />
            </IconButton>

            {/* Counter */}
            <Typography sx={{ position: "absolute", top: 18, left: 20, color: "rgba(255,255,255,0.7)", fontVariantNumeric: "tabular-nums", letterSpacing: 1 }}>
              {active + 1} / {images.length}
            </Typography>

            {/* Prev */}
            {images.length > 1 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous photo"
                sx={{ position: "absolute", left: { xs: 6, sm: 24 }, color: "white", bgcolor: "rgba(255,255,255,0.08)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }, p: 1.5 }}
              >
                <ChevronLeftRoundedIcon sx={{ fontSize: 40 }} />
              </IconButton>
            )}

            {/* Image + caption + download (swipeable) */}
            <Box
              component={motion.div}
              key={current.apiid}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={(e, info) => {
                if (info.offset.x < -80) next();
                else if (info.offset.x > 80) prev();
              }}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.22 }}
              onClick={(e) => e.stopPropagation()}
              sx={{ maxWidth: "92vw", maxHeight: "88vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, cursor: "grab", "&:active": { cursor: "grabbing" } }}
            >
              <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 120, minHeight: 120 }}>
                {!lowResLoaded && (
                  <CircularProgress sx={{ position: "absolute", color: "rgba(255,255,255,0.85)" }} />
                )}
                {/* Base layer: the same w=700 thumbnail the grid already cached, so it
                    paints instantly — no blank/blurry wait. Defines the box size. */}
                <Box
                  component="img"
                  src={mediaThumb(current.ImageUrl, 700)}
                  alt={current.Caption || gallery.Title}
                  draggable={false}
                  onLoad={() => setLowResLoaded(true)}
                  onError={() => setLowResLoaded(true)}
                  sx={{
                    display: "block",
                    maxWidth: "92vw",
                    maxHeight: "78vh",
                    objectFit: "contain",
                    borderRadius: 1,
                    boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
                  }}
                />
                {/* Sharp layer: ~1400px, fades in over the base once loaded. */}
                <Box
                  component="img"
                  src={mediaThumb(current.ImageUrl, 1400)}
                  alt=""
                  aria-hidden
                  draggable={false}
                  onLoad={() => setHiResLoaded(true)}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    borderRadius: 1,
                    opacity: hiResLoaded ? 1 : 0,
                    transition: "opacity .3s ease",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, color: "white" }}>
                {current.Caption && (
                  <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }} noWrap>{current.Caption}</Typography>
                )}
                <Tooltip title="Download full-resolution photo">
                  <IconButton onClick={() => downloadImage(current.ImageUrl, current.Caption)} sx={dlBtnSx} aria-label="Download photo">
                    <FileDownloadRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Next */}
            {images.length > 1 && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next photo"
                sx={{ position: "absolute", right: { xs: 6, sm: 24 }, color: "white", bgcolor: "rgba(255,255,255,0.08)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" }, p: 1.5 }}
              >
                <ChevronRightRoundedIcon sx={{ fontSize: 40 }} />
              </IconButton>
            )}
          </Box>
        )}
      </AnimatePresence>
    </Container>
  );
}
